import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Search, Tags } from "lucide-react";
import type { TagCount } from "@hive/sdk";
import { hive } from "../../lib/apiClient";
import { topicPath } from "../../lib/topics";
import { usePageData } from "../../ssr/usePageData";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { RetryMessage } from "./ExploreShelves";

export function AllTags() {
	const { t, i18n } = useTranslation();
	const page = usePageData();
	const [tags, setTags] = useState<TagCount[] | null>(page?.catalogueTags ?? null);
	const [failed, setFailed] = useState(false);
	const [retry, setRetry] = useState(0);
	const [query, setQuery] = useState("");
	useEffect(() => {
		if (page?.catalogueTags && retry === 0) return;
		let active = true;
		setFailed(false);
		void hive.tags.list(100).then((items) => { if (active) setTags(items); })
			.catch(() => { if (active) setFailed(true); });
		return () => { active = false; };
	}, [retry, page?.catalogueTags]);
	// Keep each word's size stable while filtering; counts only determine visual emphasis.
	const weights = new Map([...(tags ?? [])].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "en"))
		.map((tag, index, all) => [tag.name, index < all.length * .08 ? "xl" : index < all.length * .22 ? "lg"
			: index < all.length * .48 ? "md" : index < all.length * .76 ? "sm" : "xs"]));
	const visible = [...(tags ?? [])].sort((a, b) => a.name.localeCompare(b.name, i18n.language))
		.map((tag, index) => ({ ...tag, tone: index % 4 }))
		.filter((tag) => tag.name.toLowerCase().includes(query.trim().toLowerCase()));
	return <section className="all-tags">
		<div className="all-tags-heading">
			<div><h1>{t("discover.allTags")}</h1><p>{t("discover.allTagsDescription")}</p></div>
			<label className="all-tags-filter"><Search size={16} aria-hidden="true" />
				<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("discover.findTag")} aria-label={t("discover.findTag")} />
			</label>
		</div>
		{failed ? <RetryMessage onRetry={() => setRetry((value) => value + 1)} />
			: !tags ? <div role="status" className="grid h-48 place-items-center"><Spinner /><span className="sr-only">{t("common.loading")}</span></div>
			: !visible.length ? <EmptyState icon={Tags} title={t("discover.noTags")} />
			: <ul className="all-tags-cloud" aria-label={t("discover.allTags")}>{visible.map((tag) => <li key={tag.name}>
				<Link to={topicPath(tag.name)} data-weight={weights.get(tag.name)} data-tone={tag.tone}>{tag.name}</Link>
			</li>)}</ul>}
	</section>;
}
